import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const TOOLTIP_GAP_PIXELS = 10;

interface TooltipPosition {
  top: number;
  left: number;
}

interface SideTooltipProps {
  label: ReactNode;
  isEnabled?: boolean;
  className?: string;
  children: ReactNode;
}

export function SideTooltip({ label, isEnabled = true, className, children }: SideTooltipProps) {
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  function showNextTo(element: Element) {
    const bounds = element.getBoundingClientRect();
    setPosition({ top: bounds.top + bounds.height / 2, left: bounds.right + TOOLTIP_GAP_PIXELS });
  }

  function hide() {
    setPosition(null);
  }

  return (
    <div
      className={cn('block', className)}
      onPointerEnter={function showOnHover(event) {
        showNextTo(event.currentTarget);
      }}
      onPointerLeave={hide}
      onFocusCapture={function showOnFocus(event) {
        showNextTo(event.currentTarget);
      }}
      onBlurCapture={hide}
    >
      {children}
      {isEnabled && position !== null
        ? createPortal(
            <div
              aria-hidden="true"
              className="fixed z-50 -translate-y-1/2 pointer-events-none px-3 py-1.5 rounded-lg bg-zooindigo text-zoowhite text-xs font-semibold shadow-lg whitespace-nowrap"
              style={{ top: position.top, left: position.left }}
            >
              {label}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
